// src/components/proposal/ComplianceChecklist.tsx
// Checklist component for displaying compliance framework status

import React from 'react';
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Lock,
  Users,
  Eye,
  Server,
} from 'lucide-react';
import type { ComplianceFramework } from '../../types/deliverables';

interface ComplianceChecklistProps {
  compliance?: ComplianceFramework;
  className?: string;
}

interface ComplianceItem {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  applicable: boolean;
  items?: string[];
}

export function ComplianceChecklist({ compliance, className = '' }: ComplianceChecklistProps) {
  if (!compliance) {
    return (
      <div className={`bg-bw-surface-subtle rounded-lg p-4 text-center text-bw-muted ${className}`}>
        No compliance data available
      </div>
    );
  }

  const complianceItems: ComplianceItem[] = [
    {
      id: 'section_508',
      name: 'Section 508',
      description: 'Electronic content accessibility requirements (WCAG 2.1 AA)',
      icon: Eye,
      applicable: compliance.section_508,
    },
    {
      id: 'far_clauses',
      name: 'FAR Clauses',
      description: 'Federal Acquisition Regulation requirements',
      icon: FileText,
      applicable: compliance.far_clauses?.length > 0,
      items: compliance.far_clauses,
    },
    {
      id: 'dfar_clauses',
      name: 'DFAR Clauses',
      description: 'Defense Federal Acquisition Regulation Supplement',
      icon: Shield,
      applicable: compliance.dfar_clauses?.length > 0,
      items: compliance.dfar_clauses,
    },
    {
      id: 'sca',
      name: 'Service Contract Act (SCA)',
      description: 'Service Contract Labor Standards requirements',
      icon: Users,
      applicable: compliance.sca_applicable,
    },
    {
      id: 'privacy_act',
      name: 'Privacy Act',
      description: 'PII handling and privacy requirements',
      icon: Lock,
      applicable: compliance.privacy_act,
    },
    {
      id: 'fisma',
      name: 'FISMA',
      description: 'Federal Information Security Modernization Act',
      icon: Server,
      applicable: compliance.fisma,
    },
  ];

  // Add agency-specific items
  if (compliance.agency_specific?.length > 0) {
    complianceItems.push({
      id: 'agency_specific',
      name: 'Agency-Specific',
      description: 'Agency-specific compliance requirements',
      icon: AlertTriangle,
      applicable: true,
      items: compliance.agency_specific,
    });
  }

  const applicableCount = complianceItems.filter(item => item.applicable).length;

  return (
    <div className={`bg-bw-surface rounded-xl border border-bw-border overflow-hidden ${className}`}>
      <div className="bg-bw-surface-subtle px-4 py-3 border-b border-bw-border">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-bw-ink flex items-center gap-2">
            <Shield className="h-5 w-5 text-fit-moderate" />
            Compliance Framework
          </h3>
          <span className="text-sm text-bw-muted">
            {applicableCount} of {complianceItems.length} applicable
          </span>
        </div>
      </div>

      <div className="p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {complianceItems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className={`rounded-lg p-3 border ${
                  item.applicable
                    ? 'bg-success-bg border-success-border'
                    : 'bg-bw-surface-subtle border-bw-border'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`p-1.5 rounded-full ${
                      item.applicable ? 'bg-success-bg' : 'bg-bw-surface-subtle'
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 ${
                        item.applicable ? 'text-success' : 'text-bw-muted'
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium text-sm ${
                          item.applicable ? 'text-success' : 'text-bw-muted'
                        }`}
                      >
                        {item.name}
                      </span>
                      {item.applicable ? (
                        <CheckCircle className="h-4 w-4 text-success" />
                      ) : (
                        <XCircle className="h-4 w-4 text-bw-muted" />
                      )}
                    </div>
                    <p
                      className={`text-xs mt-0.5 ${
                        item.applicable ? 'text-success' : 'text-bw-muted'
                      }`}
                    >
                      {item.description}
                    </p>
                    {item.items && item.items.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {item.items.slice(0, 5).map((clause, i) => (
                          <span
                            key={i}
                            className={`inline-flex px-1.5 py-0.5 rounded text-xs ${
                              item.applicable
                                ? 'bg-success-bg text-success'
                                : 'bg-bw-surface-subtle text-bw-muted'
                            }`}
                          >
                            {clause}
                          </span>
                        ))}
                        {item.items.length > 5 && (
                          <span className="text-xs text-bw-muted">
                            +{item.items.length - 5} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* SCA Warning */}
        {compliance.sca_applicable && (
          <div className="mt-4 bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-fit-moderate flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-fit-moderate">
                  SCA Wage Determination Required
                </p>
                <p className="text-xs text-fit-moderate mt-1">
                  This contract is subject to Service Contract Act requirements. Labor rates
                  must comply with applicable SCA wage determinations for the place of performance.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ComplianceChecklist;
